<template>
   <div class="bg-white q-pa-md">
      <h1 class="text-left text-h4">Create Visual Variable</h1>
      <p id="explainer-text" class="text-left">
         A variable is the basic building block of a user experience. It is
         the thing you want to change, test, and improve upon. You can
         <a
            href="https://docs.ezbot.ai/reference-materials/glossary/"
            target="_blank"
         >
            read more about them here.</a
         >
      </p>
      <EditVisualVariable
         :variable="localVariable"
         mode="create"
         @variable-submission="handleSubmit"
      />
   </div>
</template>
<script setup lang="ts">
import EditVisualVariable from "../components/EditVisualVariable.vue"
import { useQuasar } from "quasar"
import { ref, Ref, onMounted, nextTick } from "vue"
import { useProjectStore } from "../stores/projectStore"
import { useRouter } from "vue-router"
import { toast } from "vue3-toastify"
import { EditableVariable } from "../types/variable"
import { AxiosError } from "axios"
import { collectError } from "../utils"
import { useTour } from "@/composables/useTour"
import { visualVariableTour } from "@/tours/visualVariableTour"
import { v4 as uuidv4 } from "uuid"

const projectStore = useProjectStore()
const router = useRouter()
const $q = useQuasar()

// Initialize the tour
const { autoStartTour } = useTour(visualVariableTour)

const localVariable: Ref<EditableVariable> = ref({
   humanReadableName: "",
   type: "visual",
   version: "0.2",
   key: uuidv4(),
   defaultValue: "",
   constraints: {
      enumerables: [],
   },
   config: null,
   projectId: projectStore.projectId!,
   constraintsVersion: "0.1",
})

onMounted(async () => {
   await nextTick()
   await autoStartTour()
})

const handleSubmit = async (variable: EditableVariable) => {
   $q.dialog({
      title: `Create Variable`,
      message: `Your dashboard analytics may not update for 24 hours. Are you sure you want to create this variable?`,
      cancel: true,
   }).onOk(async () => {
      try {
         $q.loadingBar.start()
         await projectStore.createVariable(variable)
         $q.loadingBar.increment(50)
         projectStore.variables = await projectStore.getVariablesByProjectId()
         $q.loadingBar.stop()
         router.push({ name: "Variables" }).then(() => {
            toast.success("Variable created.")
         })
      } catch (error) {
         collectError(error as Error)
         if (error instanceof AxiosError) {
            const msg = error.response?.data.message
            if (msg) {
               toast.error(msg)
               return
            }
         }
         toast.error("Failed to create variable.")
         return
      } finally {
         $q.loadingBar.stop()
      }
   })
}
</script>
<style lang="scss" scoped>
h3 {
   margin: 0;
}
.q-input {
   margin-bottom: 10px;
}
.q-select {
   margin-bottom: 10px;
}

#explainer-text {
   max-width: 600px;
   margin: 12px 0;
}

#variable-edit-form {
   text-align: left;
   max-width: 500px;
   display: flex;
   flex-direction: column;
}

.value-editor-container {
   background-color: #f5f5f5;
   padding: 16px;
   border-radius: 4px;
   margin-bottom: 16px;
   .value-row {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
   }
   .value-list {
      margin-top: 8px;
   }
}
</style>